/** @param {foundry["documents"]["TokenDocument"]} TokenDocument */
export const HSSTokenDocument = TokenDocument =>
	class extends TokenDocument {
		_initializeSource(data, options) {
			if (this.parent?.grid.isGridless)
				data.shape ??=
					foundry.utils.getProperty(data, "flags.hex-size-support.gridlessShape") ??
					game.settings.get("hex-size-support", "defaultGridlessShape");
			if (this.parent?.grid.isHexagonal)
				data.shape ??=
					foundry.utils.getProperty(data, "flags.hex-size-support.hexShape") ??
					game.settings.get("hex-size-support", "defaultHexShape");
			return super._initializeSource(data, options);
		}
	};

/** @param {foundry["canvas"]["placeables"]["Token"]} Token */
export const HSSToken = Token =>
	class extends Token {
		_refreshState() {
			const isSecret = this.document.isSecret;
			const isHover = this.hover || this.layer.highlightObjects;
			super._refreshState();

			/** @type {"default" | "front" | "behind"} */
			const borderPosition = game.settings.get("hex-size-support", "borderPosition");
			if (borderPosition === "front") this.border.zIndex = Infinity;
			else if (borderPosition === "behind") this.border.zIndex = -1;

			/** @type {boolean} */
			const hideBorder = this.document.getFlag("hex-size-support", "hideBorder");
			/** @type {"off" | "always" | "combat" | "never"} */
			const borderMode = hideBorder ? "never" : game.settings.get("hex-size-support", "borderMode");
			switch (borderMode) {
				case "always":
					this.border.visible = true;
					break;
				case "combat":
					this.border.visible ||= !isSecret && this.document.inCombat;
					break;
				case "never":
					this.border.visible = false;
			}

			/** @type {boolean} */
			const hideFill = this.document.getFlag("hex-size-support", "hideFill");
			/** @type {"always" | "combat" | "control" | "hover" | "none" | "owner" | "owner_hov"} */
			const fillMode = hideFill ? "none" : game.settings.get("hex-size-support", "fillBorder");
			switch (fillMode) {
				case "control":
					this.borderFill.visible = this.controlled;
					break;
				case "always":
					this.borderFill.visible = !isSecret;
					break;
				case "hover":
					this.borderFill.visible = !isSecret && isHover;
					break;
				case "owner_hov":
					this.borderFill.visible = this.isOwner && isHover;
					break;
				case "owner":
					this.borderFill.visible = this.isOwner;
					break;
				case "combat":
					this.borderFill.visible = !isSecret && this.document.inCombat;
					break;
				default:
					this.borderFill.visible = false;
					break;
			}
			this.borderFill.tint = this.getDispositionColor();
			this.borderFill.zIndex = -1;
		}

		async _draw(options) {
			await super._draw(options);
			this.borderFill ||= this.addChildAt(new PIXI.Graphics(), 0);
		}

		_refreshBorder() {
			this.#drawCustomBorder();
			if (game.settings.get("hex-size-support", "fillBorder") === "none") return;
			this.borderFill.clear().beginFill(0xffffff, 0.3).drawShape(this.shape);
		}

		#drawCustomBorder() {
			/** @type {"bevel"|"flat"|"default"} */
			const style = game.settings.get("hex-size-support", "borderStyle");

			switch (style) {
				case "bevel":
					return this.#refreshBorder_bevel();
				case "flat":
					return this.#refreshBorder_flat();
				default:
					return super._refreshBorder();
			}
		}

		#refreshBorder_flat() {
			const thickness = CONFIG.Canvas.objectBorderThickness * canvas.dimensions.uiScale;
			this.border
				.clear()
				.lineStyle({
					width: thickness,
					color: 0xffffff,
					alignment: 0,
					join: PIXI.LINE_JOIN.MITER,
				})
				.drawShape(this.shape);
		}

		#refreshBorder_bevel() {
			const thickness = CONFIG.Canvas.objectBorderThickness * canvas.dimensions.uiScale;
			this.border
				.clear()
				.lineStyle({
					width: thickness,
					color: 0xffffff,
					alignment: 0.4,
					join: PIXI.LINE_JOIN.MITER,
				})
				.drawShape(this.shape)
				.lineStyle({
					width: thickness / 3,
					color: 0x888888,
					alignment: 1.34,
					join: PIXI.LINE_JOIN.MITER,
				})
				.drawShape(this.shape);
		}
	};

/** @param {foundry["documents"]["TokenDocument"]} TokenDocument */
export const EmberHSSTokenDocument = TokenDocument => {
	const isVista = Symbol();
	return class extends TokenDocument {
		_initializeSource(data, options) {
			this.parent[isVista] ??= Object.values(ember.scenes.vistas)
				.map(s => s.id)
				.includes(this.parent.id);
			if (this.parent[isVista]) data.shape ??= CONST.TOKEN_SHAPES.RECTANGLE_1;
			return super._initializeSource(data, options);
		}

		prepareBaseData() {
			super.prepareBaseData();
			this.parent[isVista] ??= Object.values(ember.scenes.vistas)
				.map(s => s.id)
				.includes(this.parent.id);
			if (this.parent[isVista]) {
				foundry.utils.mergeObject(this.flags, { "hex-size-support.hideFill": true });
			}
		}
	};
};

/** @param {foundry["documents"]["TokenDocument"]} TokenDocument */
export const SimpleVistaHSSTokenDocument = TokenDocument =>
	class extends TokenDocument {
		_initializeSource(data, options) {
			if (this.parent.getFlag("simple-vistas", "isVista"))
				data.shape ??= CONST.TOKEN_SHAPES.RECTANGLE_1;
			return super._initializeSource(data, options);
		}

		prepareBaseData() {
			super.prepareBaseData();
			if (this.parent.getFlag("simple-vistas", "isVista")) {
				foundry.utils.mergeObject(this.flags, { "hex-size-support.hideFill": true });
			}
		}
	};
